/**
 * Hook to fetch and manage assets from API
 * Provides all asset operations with confirmation dialogs and loading states
 * Follows the same pattern as useWorkflowVersions
 * Updated to use new upload API with base64 encoding
 * Supports presigned URL uploads for large files (>10MB) to bypass API body limits
 */

import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { Asset, AssetWithDerived, fileToBase64 } from "@/types/asset.types";
import {
  listAssets,
  getAsset,
  undeleteAsset,
  purgeAsset,
  uploadAsset as uploadAssetApi,
  initiateAssetUpload,
  confirmAssetUpload,
} from "@/services/api/sdk.gen";
import { logger } from "@/services/logger";
import { generateUUID } from "@/utils/uuid";
import { useConfirmationDialog } from "./useConfirmationDialog";
import { useLoadingDialog } from "@/components/shared/LoadingDialog";
import { AssetFilename } from "@/components/assets/AssetFilename";
import { toast } from "sonner";
import { showErrorToast } from "@/utils/showErrorToast";
import { useMemo, useCallback } from "react";
import { useAssetDelete } from "./useAssetDelete";
import { transformEntitlements } from "@/utils/entitlements";
import { useAppStore } from "@/stores/appStore";

/**
 * Threshold for switching to presigned URL upload (10MB).
 * Files larger than this will use direct S3 upload to avoid API body size limits.
 *
 * IMPORTANT: This value MUST match UPLOAD_LIMITS.BASE64_TO_PRESIGNED_BYTES in
 * api-alpha/src/constants/uploadLimits.ts. The API bodyLimit is set to ~20MB
 * to accommodate this 10MB file + base64 inflation (~33%) + JSON overhead.
 *
 * @see docs-alpha/development/planning/platform-hardening/implementation/03-asset-upload-hardening.md
 */
const PRESIGNED_UPLOAD_THRESHOLD_BYTES = 10 * 1024 * 1024;

// Default page size for pagination
const DEFAULT_PAGE_SIZE = 20;

// Page size for fetching all assets
const FETCH_ALL_PAGE_SIZE = 100;

// Maximum pages to fetch (safety cap: 20 pages × 100 = 2000 assets max)
const MAX_PAGES = 20;

// ============================================================================
// UPLOAD HELPER FUNCTIONS
// ============================================================================

/**
 * Upload small file using base64 encoding through API
 */
async function uploadSmallFile(
  file: File,
  projectId: string,
  workflowId: string | undefined,
  correlationId: string,
): Promise<Asset> {
  // Convert file to base64
  const fileData = await fileToBase64(file);

  logger.info("File converted to base64", {
    correlationId,
    fileName: file.name,
  });

  // Upload asset with base64 data
  const result = await uploadAssetApi({
    path: { projectId },
    body: {
      fileName: file.name,
      fileData,
      mimeType: file.type,
      ...(workflowId && { workflowId }),
    },
    throwOnError: true,
  });

  const uploadedAsset = result?.data?.data as { assetId?: string } | undefined;

  logger.info("Asset uploaded successfully (base64)", {
    correlationId,
    assetId: uploadedAsset?.assetId,
    fileName: file.name,
  });

  // Fetch the complete asset details to get downloadUrl
  if (uploadedAsset?.assetId) {
    const assetDetails = await getAsset({
      path: { assetId: uploadedAsset.assetId },
      throwOnError: true,
    });
    return assetDetails?.data?.data as Asset;
  }

  throw new Error("Upload succeeded but no asset ID returned");
}

/**
 * Upload large file using presigned URL for direct S3 upload.
 * Supports both PUT (legacy) and POST (with content-length-range) methods.
 * Flow: initiate-upload -> S3 PUT/POST -> confirm-upload
 */
async function uploadLargeFile(
  file: File,
  projectId: string,
  workflowId: string | undefined,
  correlationId: string,
): Promise<Asset> {
  // Step 1: Initiate upload to get presigned URL
  logger.info("Initiating presigned URL upload", {
    correlationId,
    fileName: file.name,
    fileSize: file.size,
  });

  const initiateResult = await initiateAssetUpload({
    path: { projectId },
    body: {
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
      ...(workflowId && { workflowId }),
    },
    throwOnError: true,
  });

  const initiateData = initiateResult?.data?.data as
    | {
        assetId?: string;
        uploadUrl?: string;
        uploadFields?: Record<string, string>;
        uploadMethod?: "PUT" | "POST";
        filePath?: string;
      }
    | undefined;

  const {
    assetId,
    uploadUrl,
    uploadFields,
    uploadMethod = "PUT",
  } = initiateData || {};

  if (!assetId || !uploadUrl) {
    throw new Error("Initiate upload response missing assetId or uploadUrl");
  }

  logger.info("Presigned URL obtained", {
    correlationId,
    assetId,
    fileName: file.name,
    uploadMethod,
  });

  // Step 2: Upload file directly to S3 using presigned URL
  logger.info("Uploading file to S3 via presigned URL", {
    correlationId,
    assetId,
    fileName: file.name,
    fileSize: file.size,
    uploadMethod,
  });

  let s3Response: Response;

  if (uploadMethod === "POST" && uploadFields) {
    // POST with FormData (supports content-length-range enforcement)
    const formData = new FormData();

    // Add all presigned fields first (order matters for S3)
    Object.entries(uploadFields).forEach(([key, value]) => {
      formData.append(key, value);
    });

    // Add file last (required by S3 presigned POST)
    formData.append("file", file);

    s3Response = await fetch(uploadUrl, {
      method: "POST",
      body: formData,
      // Note: Don't set Content-Type header - browser will set it with boundary
    });
  } else {
    // PUT (legacy, no size enforcement)
    s3Response = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": file.type,
      },
    });
  }

  if (!s3Response.ok) {
    // Parse S3 error if possible for better error messages
    let errorDetail = "";
    try {
      const errorText = await s3Response.text();
      // S3 returns XML errors, extract the message
      const messageMatch = errorText.match(/<Message>([^<]+)<\/Message>/);
      if (messageMatch) {
        errorDetail = ` - ${messageMatch[1]}`;
      }
    } catch {
      // Ignore parsing errors
    }
    throw new Error(
      `S3 upload failed: ${s3Response.status} ${s3Response.statusText}${errorDetail}`,
    );
  }

  logger.info("File uploaded to S3 successfully", {
    correlationId,
    assetId,
    fileName: file.name,
    uploadMethod,
  });

  // Step 3: Confirm upload to finalize asset
  logger.info("Confirming upload", {
    correlationId,
    assetId,
    fileName: file.name,
  });

  await confirmAssetUpload({
    path: { projectId },
    body: { assetId },
    throwOnError: true,
  });

  logger.info("Asset upload confirmed (presigned URL)", {
    correlationId,
    assetId,
    fileName: file.name,
  });

  // Fetch the complete asset details to get downloadUrl
  const assetDetails = await getAsset({
    path: { assetId },
    throwOnError: true,
  });

  return assetDetails?.data?.data as Asset;
}

interface UseAssetsOptions {
  /** Optional workflow ID to associate with uploaded assets */
  workflowId?: string;
  /** Whether to enable fetching assets - defaults to true for backwards compatibility */
  fetchEnabled?: boolean;
}

/**
 * Hook to fetch and manage assets from the API
 * Returns raw assets without any preprocessing
 * @param projectId - The project ID to fetch assets for
 * @param options - Optional configuration including workflowId and fetchEnabled
 */
export function useAssets(
  projectId: string | undefined,
  options?: string | UseAssetsOptions,
) {
  // Support both old signature (workflowId string) and new signature (options object)
  const { workflowId, fetchEnabled = true } =
    typeof options === "string" ? { workflowId: options } : (options ?? {});
  const queryClient = useQueryClient();
  const { confirm } = useConfirmationDialog();
  const { withLoading } = useLoadingDialog();
  const setEntitlements = useAppStore((s) => s.setEntitlements);
  const { deleteAsset: deleteAssetWithConfirmation, isDeleting } =
    useAssetDelete();

  // ============================================================================
  // ASSET LIST QUERY
  // ============================================================================

  const assetsQuery = useQuery({
    queryKey: ["assets", projectId],
    queryFn: async () => {
      if (!projectId) {
        return [];
      }

      const correlationId = generateUUID();
      const allAssets: AssetWithDerived[] = [];
      let page = 1;
      let hasNext = true;

      logger.info("Fetching all project assets", { correlationId, projectId });

      try {
        // Fetch pages sequentially until no more pages
        while (hasNext && page <= MAX_PAGES) {
          const result = await listAssets({
            path: { projectId },
            query: { page, limit: FETCH_ALL_PAGE_SIZE },
            throwOnError: true,
          });

          const data = (result?.data?.data as AssetWithDerived[]) || [];
          allAssets.push(...data);

          // Check pagination for next page. StandardResponse.meta is typed
          // as an open bag; narrow to just the fields we read here.
          const extendedData = result?.data as
            | {
                meta?: { pagination?: { hasNext?: boolean } } | null;
                entitlements?: unknown;
              }
            | undefined;
          hasNext = extendedData?.meta?.pagination?.hasNext ?? false;

          // Capture entitlements from first page only
          if (page === 1) {
            const rawEntitlements = extendedData?.entitlements ?? null;
            if (rawEntitlements) {
              if (import.meta.env.DEV) {
                const { useEntitlementDebugStore } =
                  await import("@/stores/useEntitlementDebugStore");
                useEntitlementDebugStore
                  .getState()
                  .log(
                    "set",
                    "useAssets.assetsQuery",
                    `Extracted entitlements from listAssets response (project: ${projectId})`,
                    undefined,
                    rawEntitlements,
                  );
              }
              const mapped = transformEntitlements(rawEntitlements);
              if (mapped) {
                setEntitlements(
                  mapped,
                  `useAssets → listAssets (project: ${projectId})`,
                );
              }
            }
          }

          page++;
        }

        logger.info("Assets fetched successfully", {
          count: allAssets.length,
          pages: page - 1,
          correlationId,
          projectId,
        });

        return allAssets;
      } catch (error) {
        logger.error("Failed to fetch assets", error as Error, {
          correlationId,
          projectId,
        });

        throw error;
      }
    },
    enabled: !!projectId && fetchEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes (match global default)
    // Disable automatic refetch on window focus to prevent idle polling.
    // Asset list doesn't need real-time freshness. See #1303.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 401 || status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Access the data - includes inline derivedAssets[] when available
  const assets: AssetWithDerived[] = useMemo(
    () => assetsQuery.data || [],
    [assetsQuery.data],
  );

  // ============================================================================
  // UPLOAD MUTATION - Conditional: base64 for small files, presigned URL for large
  // ============================================================================

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!projectId) {
        throw new Error("Project ID is required");
      }

      const correlationId = generateUUID();
      const useLargeFileUpload = file.size > PRESIGNED_UPLOAD_THRESHOLD_BYTES;

      logger.info("Initiating asset upload", {
        correlationId,
        projectId,
        fileName: file.name,
        contentType: file.type,
        size: file.size,
        useLargeFileUpload,
      });

      try {
        if (useLargeFileUpload) {
          // Large file: Use presigned URL upload flow
          return await uploadLargeFile(
            file,
            projectId,
            workflowId,
            correlationId,
          );
        } else {
          // Small file: Use base64 upload (existing flow)
          return await uploadSmallFile(
            file,
            projectId,
            workflowId,
            correlationId,
          );
        }
      } catch (error) {
        logger.error("Failed to upload asset", error as Error, {
          correlationId,
          projectId,
          fileName: file.name,
          useLargeFileUpload,
        });
        throw error;
      }
    },
    onSuccess: () => {
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: ["assets", projectId] });
        // Delayed refetch to pick up audioExtractionStatus which is set
        // asynchronously after the upload response (fire-and-forget pattern)
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ["assets", projectId] });
        }, 1500);
      }
      queryClient.invalidateQueries({ queryKey: ["projects"] }); // Refresh entitlement counters
      toast.success("Asset uploaded successfully!");
    },
    onError: (error) => {
      showErrorToast({
        message: "Failed to upload asset",
        error,
        projectId,
        workflowId,
      });
    },
  });

  // ============================================================================
  // UNDELETE MUTATION
  // ============================================================================

  const undeleteMutation = useMutation({
    mutationFn: async (assetId: string) => {
      const correlationId = generateUUID();

      logger.info("Restoring asset", {
        correlationId,
        projectId,
        assetId,
      });

      try {
        const result = await undeleteAsset({
          path: { assetId },
          throwOnError: true,
        });

        logger.info("Asset restored successfully", {
          correlationId,
          assetId,
        });

        return result?.data?.data as Asset;
      } catch (error) {
        logger.error("Failed to restore asset", error as Error, {
          correlationId,
          assetId,
        });
        throw error;
      }
    },
    onSuccess: () => {
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: ["assets", projectId] });
      }
      queryClient.invalidateQueries({ queryKey: ["projects"] }); // Refresh entitlement counters
      toast.success("Asset restored successfully");
    },
    onError: (error) => {
      showErrorToast({
        message: "Failed to restore asset",
        error,
        projectId,
      });
    },
  });

  // ============================================================================
  // PURGE MUTATION
  // ============================================================================

  const purgeMutation = useMutation({
    mutationFn: async (assetId: string) => {
      const correlationId = generateUUID();

      logger.info("Permanently deleting asset", {
        correlationId,
        projectId,
        assetId,
      });

      try {
        await purgeAsset({
          path: { assetId },
          throwOnError: true,
        });

        logger.info("Asset permanently deleted", {
          correlationId,
          assetId,
        });
      } catch (error) {
        logger.error("Failed to permanently delete asset", error as Error, {
          correlationId,
          assetId,
        });
        throw error;
      }
    },
    onSuccess: () => {
      if (projectId) {
        queryClient.invalidateQueries({
          queryKey: ["assets", projectId],
        });
      }
      toast.success("Asset permanently deleted");
    },
    onError: (error) => {
      showErrorToast({
        message: "Failed to permanently delete asset",
        error,
        projectId,
      });
    },
  });

  // ============================================================================
  // WRAPPER FUNCTIONS WITH CONFIRMATION AND LOADING
  // ============================================================================

  const uploadAsset = async (file: File): Promise<Asset | null> => {
    return withLoading(
      {
        title: "Uploading Asset",
        message: `Uploading ${file.name}...`,
      },
      async () => {
        const asset = await uploadMutation.mutateAsync(file);
        return asset;
      },
    );
  };

  const removeAsset = (asset: Asset): Promise<boolean> =>
    deleteAssetWithConfirmation(
      asset,
      projectId ?? asset.projectId ?? undefined,
      {
        throwOnError: true,
      },
    );

  const restoreAsset = async (asset: Asset): Promise<boolean> => {
    const confirmed = await confirm({
      title: (
        <span className="flex items-center gap-2">
          Restore{" "}
          <AssetFilename fileName={asset.filename} maxWidth="max-w-[300px]" />?
        </span>
      ),
      message: "This will restore the asset from trash.",
      confirmText: "Restore",
      cancelText: "Cancel",
    });

    if (!confirmed) return false;

    await undeleteMutation.mutateAsync(asset.id);
    return true;
  };

  const permanentlyDeleteAsset = async (asset: Asset): Promise<boolean> => {
    const confirmed = await confirm({
      title: (
        <span className="flex items-center gap-2">
          Permanently delete
          <AssetFilename fileName={asset.filename} maxWidth="max-w-[250px]" />?
        </span>
      ),
      message:
        "This action cannot be undone. The asset will be permanently deleted.",
      confirmText: "Delete Forever",
      cancelText: "Cancel",
    });

    if (!confirmed) return false;

    return withLoading(
      {
        title: "Deleting Asset",
        message: `Permanently deleting ${asset.filename}...`,
      },
      async () => {
        await purgeMutation.mutateAsync(asset.id);
        return true;
      },
    );
  };

  const getAssetById = useCallback(
    async (assetId: string): Promise<Asset | null> => {
      const correlationId = generateUUID();

      try {
        const result = await getAsset({
          path: { assetId },
          throwOnError: true,
        });

        return result?.data?.data as Asset;
      } catch (error) {
        logger.error("Failed to fetch asset details", error as Error, {
          correlationId,
          assetId,
        });
        return null;
      }
    },
    [],
  );

  // ============================================================================
  // RETURN HOOK DATA
  // ============================================================================

  return {
    // Raw assets from API
    assets,
    refetchAssets: assetsQuery.refetch,
    isLoading: assetsQuery.isLoading,
    error: assetsQuery.error,

    // Mutation states
    isUploading: uploadMutation.isPending,
    isDeleting,
    isRestoring: undeleteMutation.isPending,
    isPurging: purgeMutation.isPending,

    // Public wrapper functions
    uploadAsset,
    removeAsset,
    restoreAsset,
    permanentlyDeleteAsset,
    getAssetById,
  };
}

// ============================================================================
// INFINITE QUERY HOOK FOR PAGINATION
// ============================================================================

interface InfiniteAssetsOptions {
  projectId: string | undefined;
  limit?: number;
}

/**
 * Hook for fetching assets with infinite scroll pagination.
 * Returns paginated data with fetchNextPage for infinite scroll UI.
 */
export function useInfiniteAssets(options: InfiniteAssetsOptions) {
  const { projectId, limit = DEFAULT_PAGE_SIZE } = options;
  const queryClient = useQueryClient();
  const setEntitlements = useAppStore((s) => s.setEntitlements);

  const query = useInfiniteQuery({
    queryKey: ["assets", "infinite", projectId, limit],
    queryFn: async ({ pageParam = 1 }) => {
      if (!projectId) {
        return { data: [], meta: { page: 1, limit, total: 0, hasMore: false } };
      }

      const correlationId = generateUUID();

      logger.info("Fetching assets page", {
        correlationId,
        projectId,
        page: pageParam,
        limit,
      });

      try {
        const result = await listAssets({
          path: { projectId },
          query: { page: pageParam, limit },
          throwOnError: true,
        });

        const responseData = result?.data as any;
        const assetsData = (responseData?.data as AssetWithDerived[]) || [];
        const meta = responseData?.meta || {};

        // Capture entitlements from first page
        if (pageParam === 1) {
          const rawEntitlements = responseData?.entitlements ?? null;
          if (rawEntitlements) {
            if (import.meta.env.DEV) {
              const { useEntitlementDebugStore } =
                await import("@/stores/useEntitlementDebugStore");
              useEntitlementDebugStore
                .getState()
                .log(
                  "set",
                  "useInfiniteAssets",
                  `Extracted entitlements from listAssets response (project: ${projectId})`,
                  undefined,
                  rawEntitlements,
                );
            }
            const mapped = transformEntitlements(rawEntitlements);
            if (mapped) {
              setEntitlements(
                mapped,
                `useInfiniteAssets → listAssets (project: ${projectId})`,
              );
            }
          }
        }

        logger.info("Assets page fetched successfully", {
          count: assetsData.length,
          page: pageParam,
          hasMore: meta.hasMore,
          correlationId,
          projectId,
        });

        return {
          data: assetsData,
          meta: {
            page: pageParam,
            limit,
            total: meta.total || 0,
            hasMore: meta.hasMore ?? assetsData.length >= limit,
          },
        };
      } catch (error) {
        logger.error("Failed to fetch assets page", error as Error, {
          correlationId,
          projectId,
          page: pageParam,
        });
        throw error;
      }
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.meta.hasMore ? lastPage.meta.page + 1 : undefined;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes (match global default)
    // Disable automatic refetch on window focus to prevent idle polling.
    // Asset list doesn't need real-time freshness. See #1303.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Flatten all pages into a single array
  const assets: AssetWithDerived[] = useMemo(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data],
  );

  // Get total from the first page's meta
  const total = query.data?.pages[0]?.meta.total ?? 0;

  // Invalidate helper for after mutations
  const invalidateAssets = useCallback(() => {
    if (projectId) {
      queryClient.invalidateQueries({
        queryKey: ["assets", "infinite", projectId],
      });
      // Also invalidate the non-infinite query for backward compatibility
      queryClient.invalidateQueries({ queryKey: ["assets", projectId] });
    }
  }, [queryClient, projectId]);

  return {
    assets,
    total,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    invalidateAssets,
  };
}
